import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	NgModule,
	OnInit,
	Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
	MatCheckboxChange,
	MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
	MAT_TOOLTIP_DEFAULT_OPTIONS,
	MatTooltipDefaultOptions,
	MatTooltipModule,
} from '@angular/material/tooltip';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { CoreIcons } from '@datachain/ui-sdk/components/dc-icon';
import { DcButtonModule } from '@datachain/ui-sdk/components/app-button';
import { DcColumnTypeModule } from '@datachain/ui-sdk/components/app-column-type';
import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { DcHighlightDirectiveModule } from '@datachain/ui-sdk/directives';
import { DcTruncatePipeModule } from '@datachain/ui-sdk/pipes';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	Subject,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { DcIcons } from '../../../ui/app-dc.icons';
import { HeaderMappingItemEntity } from '../header-mapping-modal/header-mapping-item.entity';
import {
	DepotSchemaEntity,
	DepotSchemaId,
	EMPTY_SCHEMA_HEADER_LABEL,
	SchemaDataStructure,
	SchemaSelectedHeader,
} from './depot-schema.entity';

type DepotMappingConfig = {
	conf: DepotSchemaEntity;
	isEditActive: boolean;
	isFolded: boolean;
};

/** Custom options the configure the tooltip's default show/hide delays. */
export const customTooltipDefaults: MatTooltipDefaultOptions = {
	showDelay: 0,
	hideDelay: 0,
	touchendHideDelay: 0,
	position: 'above',
};

@Component({
	selector: 'app-depot-schema',
	templateUrl: './depot-schema-viewer.component.html',
	styleUrls: ['./depot-schema-viewer.component.scss'],
	inputs: [
		'structure',
		'searchTerm',
		'expandAll',
		'collapseAll',
		'selectLeafs',
		'replaceDuplicates',
		'resetLabels',
		'replaceWithPath',
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
			useValue: customTooltipDefaults,
		},
	],
})
export class DepotSchemaViewerComponent
	extends DcBaseComponent
	implements OnInit
{
	public DataStruct = SchemaDataStructure;
	public CoreIcons = CoreIcons;
	public DcIcons = DcIcons;
	public EMPTY_LABEL_PLACEHOLDER = $localize`:i18n=@@depot.emptyLabel:`;

	private originalStructure = new Array<DepotSchemaEntity>();

	private readonly filteredStructMapSubject = new BehaviorSubject<
		Map<DepotSchemaId, DepotMappingConfig>
	>(Map());
	private readonly filteredStructSubject = new BehaviorSubject<
		Array<DepotSchemaEntity>
	>([]);
	private readonly schemaAnalysisSubject = new BehaviorSubject<
		Map<string, Set<string>>
	>(Map());

	private readonly markedAsDuplicatedSubject = new BehaviorSubject<Set<string>>(
		Set()
	);

	private readonly duplicateComputationTrigger$ = new Subject<void>();

	public vo$!: Observable<{
		structure: Array<DepotSchemaEntity>;
		duplicateAnalysis: Map<string, Set<string>>;
		editMap: Map<DepotSchemaId, DepotMappingConfig>;
		foundDuplicates: Set<string>;
		searchTerm: string;
	}>;

	@Input()
	public maxOptionLength = 25;

	@Output()
	public structureValidity = new EventEmitter<Array<string>>();

	@Output()
	public selectedHeaders = new EventEmitter<Array<DepotSchemaEntity>>();

	protected readonly ColumnType = ColumnType;

	public constructor() {
		super();
	}

	public override ngOnInit(): void {
		super.ngOnInit();

		const structure$ = merge(
			of<Array<DepotSchemaEntity>>([]),
			this.toObservable<Array<DepotSchemaEntity>>('structure')
		);

		const searchTerm$ = merge(
			of(''),
			this.toObservable<string>('searchTerm').pipe(debounceTime(200))
		);

		merge(
			this.toObservable<number>('replaceDuplicates').pipe(
				map(() => 'replace-duplicate')
			),
			this.toObservable<number>('resetLabels').pipe(map(() => 'reset')),
			this.toObservable<number>('replaceWithPath').pipe(
				map(() => 'replaceWithPath')
			)
		)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(
					this.filteredStructMapSubject,
					this.markedAsDuplicatedSubject,
					this.schemaAnalysisSubject
				),
				tap(([val, currentMap, currentDuplicates, currentSchemaAnalysis]) => {
					let updatedSchemaAnalysis = Map(currentSchemaAnalysis);
					const updated = currentMap.reduce((acc, depotMappingEntry) => {
						if (
							val === 'replace-duplicate' &&
							depotMappingEntry.conf.isSelected &&
							currentDuplicates.has(`${depotMappingEntry.conf.id}`)
						) {
							const beforeUpdateLabel = depotMappingEntry.conf.currentLabel;
							depotMappingEntry.conf =
								depotMappingEntry.conf.replaceLabelWithPath();
							updatedSchemaAnalysis = this.updateSchemaAnalysis(
								beforeUpdateLabel,
								depotMappingEntry.conf,
								updatedSchemaAnalysis
							);
							return acc.set(depotMappingEntry.conf.id, depotMappingEntry);
						} else if (val === 'reset') {
							const beforeUpdateLabel = depotMappingEntry.conf.currentLabel;
							depotMappingEntry.conf = depotMappingEntry.conf.resetLabel();
							updatedSchemaAnalysis = this.updateSchemaAnalysis(
								beforeUpdateLabel,
								depotMappingEntry.conf,
								updatedSchemaAnalysis
							);
							return acc.set(depotMappingEntry.conf.id, depotMappingEntry);
						} else if (val === 'replaceWithPath') {
							const beforeUpdateLabel = depotMappingEntry.conf.currentLabel;
							depotMappingEntry.conf =
								depotMappingEntry.conf.replaceLabelWithPath(false);
							updatedSchemaAnalysis = this.updateSchemaAnalysis(
								beforeUpdateLabel,
								depotMappingEntry.conf,
								updatedSchemaAnalysis
							);
							return acc.set(depotMappingEntry.conf.id, depotMappingEntry);
						}
						return acc.set(depotMappingEntry.conf.id, depotMappingEntry);
					}, Map<DepotSchemaId, DepotMappingConfig>());
					this.filteredStructMapSubject.next(updated);
					this.schemaAnalysisSubject.next(updatedSchemaAnalysis);
					this.duplicateComputationTrigger$.next();
				})
			)
			.subscribe();

		this.toObservable<boolean>('expandAll')
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.filteredStructMapSubject),
				tap(([val, currentMap]) => {
					const updated = currentMap.map((entry) => ({
						...entry,
						isFolded: !val,
					}));
					this.filteredStructMapSubject.next(updated);
				})
			)
			.subscribe();

		this.toObservable<boolean>('collapseAll')
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.filteredStructMapSubject),
				tap(([val, currentMap]) => {
					const updated = currentMap.map((entry) => ({
						...entry,
						isFolded: !val,
					}));
					this.filteredStructMapSubject.next(updated);
				})
			)
			.subscribe();

		this.toObservable<boolean>('selectLeafs')
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.filteredStructMapSubject),
				tap(([val, currentMap]) => {
					const updated = currentMap.map((entry) => ({
						...entry,
						conf: entry.conf.selectIfLeafEntry(!!val),
					}));
					this.filteredStructMapSubject.next(updated);
					this.duplicateComputationTrigger$.next();
				})
			)
			.subscribe();

		this.duplicateComputationTrigger$
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(
					this.filteredStructMapSubject,
					this.schemaAnalysisSubject
				),
				tap(([_, currentMap, schema]) => {
					let updatedDuplicates = Set<string>();
					currentMap.forEach((entryConfig) => {
						updatedDuplicates = this.updateDuplicates(
							entryConfig,
							updatedDuplicates,
							schema,
							currentMap
						);
					});
					this.markedAsDuplicatedSubject.next(updatedDuplicates);
				})
			)
			.subscribe();

		combineAll({
			duplicates: this.markedAsDuplicatedSubject,
			currentMap: this.filteredStructMapSubject,
			schemaAnalysis: this.schemaAnalysisSubject,
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ duplicates, currentMap, schemaAnalysis }) => {
					const noDuplicatesDetected = duplicates.size === 0;
					const noEmptyLabelsSelected = !this.hasSelectedEmptyLabelHeaders(
						schemaAnalysis,
						currentMap
					);
					const hasSelection = this.countSelectedItems(currentMap) !== 0;
					const validityMsg = [];
					if (!noDuplicatesDetected) {
						validityMsg.push($localize`:i18n=@@depot.duplicateLabel:`);
					}
					if (!hasSelection) {
						validityMsg.push($localize`:i18n=@@depot.no-selection:`);
					}
					if (!noEmptyLabelsSelected) {
						validityMsg.push($localize`:i18n=@@depot.emptyLabel:`);
					}
					this.structureValidity.emit(validityMsg);
				})
			)
			.subscribe();

		structure$
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.filteredStructMapSubject),
				tap(([structure, currentMap]) => {
					this.originalStructure = [...structure];
					const analyzed = DepotSchemaEntity.analyze(structure);
					this.schemaAnalysisSubject.next(analyzed);
					this.filteredStructSubject.next([...structure]);
					if (currentMap.size === 0) {
						const map = this.flattenStructure([...structure]);
						this.filteredStructMapSubject.next(map);
					} else {
						/**
						 * TODO: on suppose la que une re-emission sur le stream structure$ ait lieu en cas d'une action de replace,
						 * d'ou l'usage de updateCurrentLabel
						 * il sera plus simple de traiter les actions possibles a travers une seule interface d'entree, plutot que de
						 passer par le store et synchronizer le dictionnaire courant a ce niveau
							*/
						const newMap = this.flattenStructure([...structure]);
						const updated = newMap.map((e, key) => {
							const previousEntry = currentMap.get(key) as DepotMappingConfig;
							return {
								...previousEntry,
								conf: previousEntry.conf.updateCurrentLabel(
									e.conf.currentLabel
								),
							};
						});
						this.filteredStructMapSubject.next(updated);
					}
				})
			)
			.subscribe();

		this.filteredStructMapSubject
			.pipe(
				debounceTime(500),
				takeUntil(this.onDestroy$),
				withLatestFrom(this.filteredStructSubject),
				tap(([currentMap, originalStructure]) => {
					const selected =
						this.filterSelectedEntriesByAppearanceWithinStructure(
							originalStructure,
							currentMap
						);
					this.selectedHeaders.emit(selected);
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			structure: this.filteredStructSubject,
			duplicateAnalysis: this.schemaAnalysisSubject,
			editMap: this.filteredStructMapSubject,
			searchTerm: searchTerm$,
			foundDuplicates: this.markedAsDuplicatedSubject,
		});
	}

	public async extractData(): Promise<{
		depotMapping: Array<DepotSchemaEntity>;
		selectedHeaders: Array<SchemaSelectedHeader>;
	}> {
		const currentSchemaMap = await firstValueFrom(
			this.filteredStructMapSubject
		);
		const originalSchemaArray = await firstValueFrom(
			this.filteredStructSubject
		);

		if (originalSchemaArray[0].schemaElemType !== 'ROOT') {
			return {
				depotMapping: [],
				selectedHeaders: [],
			};
		}
		const originalSchema = originalSchemaArray[0]
			.children as Array<DepotSchemaEntity>;
		return {
			depotMapping: this.updateOriginalSchema(originalSchema, currentSchemaMap),
			selectedHeaders: [],
		};
	}

	public async ignoreSubstructure(
		event: Event,
		id: DepotSchemaId
	): Promise<void> {
		let available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		depotMappingEntry.conf = depotMappingEntry.conf.ignoreSubtree(
			!depotMappingEntry.conf.shouldIgnoreSubtree
		);
		// synchronize map
		available = this.synchronizeIgnoreSubtree(depotMappingEntry, available);

		this.filteredStructMapSubject.next(available);
	}

	private synchronizeIgnoreSubtree(
		entry: DepotMappingConfig,
		available: Map<DepotSchemaId, DepotMappingConfig>
	): Map<DepotSchemaId, DepotMappingConfig> {
		const currentMap = available.set(entry.conf.id, entry);
		return (entry.conf.children as Array<DepotSchemaEntity>).reduce(
			(updated, c) => {
				const childConfig = updated.get(`${c.id}`) as DepotMappingConfig;
				const updatedChild = {
					...childConfig,
					conf: childConfig.conf.ignoreSubtree(
						entry.conf.shouldIgnoreSubtree,
						false
					),
				};
				updated = updated.set(`${c.id}`, updatedChild);
				if (
					c.schemaElemType === SchemaDataStructure.Struct ||
					c.schemaElemType === SchemaDataStructure.Array
				) {
					updated = this.synchronizeIgnoreSubtree(updatedChild, updated);
				}
				return updated;
			},
			currentMap
		);
	}

	public async autoFixDuplicateLabel(
		event: Event,
		id: DepotSchemaId
	): Promise<void> {
		const available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		const beforeUpdateLabel = depotMappingEntry.conf.currentLabel;
		depotMappingEntry.conf = depotMappingEntry.conf.replaceLabelWithPath(false);
		const updated = available.set(depotMappingEntry.conf.id, depotMappingEntry);
		this.filteredStructMapSubject.next(updated);

		let schemaAnalysis = await firstValueFrom(this.schemaAnalysisSubject);
		schemaAnalysis = this.updateSchemaAnalysis(
			beforeUpdateLabel,
			depotMappingEntry.conf,
			schemaAnalysis
		);
		this.schemaAnalysisSubject.next(schemaAnalysis);
		this.duplicateComputationTrigger$.next();

		event.preventDefault();
		event.stopPropagation();
	}

	public async toggleEditMode(event: Event, id: DepotSchemaId): Promise<void> {
		const available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		depotMappingEntry.isEditActive = !depotMappingEntry.isEditActive;
		const updated = available.set(depotMappingEntry.conf.id, depotMappingEntry);
		this.filteredStructMapSubject.next(updated);
		setTimeout(() => {
			const inputElement = document.getElementById(`${id}`) as HTMLInputElement;
			inputElement.focus();
		});
		event.preventDefault();
		event.stopPropagation();
	}

	public async updateCurrentLabel(
		event: Event,
		id: DepotSchemaId,
		val: string
	): Promise<void> {
		const available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		const beforeUpdateLabel = depotMappingEntry.conf.currentLabel;
		depotMappingEntry.conf = depotMappingEntry.conf.updateCurrentLabel(val);
		depotMappingEntry.isEditActive = !depotMappingEntry.isEditActive;
		const updated = available.set(depotMappingEntry.conf.id, depotMappingEntry);
		this.filteredStructMapSubject.next(updated);

		let schemaAnalysis = await firstValueFrom(this.schemaAnalysisSubject);
		schemaAnalysis = this.updateSchemaAnalysis(
			beforeUpdateLabel,
			depotMappingEntry.conf,
			schemaAnalysis
		);
		this.schemaAnalysisSubject.next(schemaAnalysis);
		this.duplicateComputationTrigger$.next();

		event.preventDefault();
		event.stopPropagation();
	}

	public async revert(event: Event, id: DepotSchemaId): Promise<void> {
		const available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		const beforeUpdateLabel = depotMappingEntry.conf.currentLabel;
		depotMappingEntry.conf = depotMappingEntry.conf.updateCurrentLabel(
			depotMappingEntry.conf.originalLabel
		);
		const updated = available.set(depotMappingEntry.conf.id, depotMappingEntry);
		this.filteredStructMapSubject.next(updated);

		let schemaAnalysis = await firstValueFrom(this.schemaAnalysisSubject);
		schemaAnalysis = this.updateSchemaAnalysis(
			beforeUpdateLabel,
			depotMappingEntry.conf,
			schemaAnalysis
		);
		this.schemaAnalysisSubject.next(schemaAnalysis);
		this.duplicateComputationTrigger$.next();
		event.preventDefault();
		event.stopPropagation();
	}

	public async updateSelection(
		event: MatCheckboxChange,
		id: DepotSchemaId
	): Promise<void> {
		const currentSchemaMap = await firstValueFrom(
			this.filteredStructMapSubject
		);
		const depotMappingEntry = currentSchemaMap.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		depotMappingEntry.conf = depotMappingEntry.conf.updateSelection(
			event.checked
		);
		const updated = currentSchemaMap.set(
			depotMappingEntry.conf.id,
			depotMappingEntry
		);
		this.filteredStructMapSubject.next(updated);
		this.duplicateComputationTrigger$.next();
	}

	public async cancelUpdate(event: Event, id: DepotSchemaId): Promise<void> {
		const available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		depotMappingEntry.isEditActive = !depotMappingEntry.isEditActive;
		const updated = available.set(depotMappingEntry.conf.id, depotMappingEntry);
		this.filteredStructMapSubject.next(updated);
		this.duplicateComputationTrigger$.next();
		event.preventDefault();
		event.stopPropagation();
	}

	public async fold(event: Event, id: DepotSchemaId): Promise<void> {
		const available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		depotMappingEntry.isFolded = !depotMappingEntry.isFolded;
		const updated = available.set(depotMappingEntry.conf.id, depotMappingEntry);
		this.filteredStructMapSubject.next(updated);
		event.preventDefault();
		event.stopPropagation();
	}

	public async updateSerialization(
		event: boolean,
		id: DepotSchemaId
	): Promise<void> {
		const available = await firstValueFrom(this.filteredStructMapSubject);
		const depotMappingEntry = available.get(`${id}`);
		if (depotMappingEntry === undefined) {
			return;
		}
		depotMappingEntry.conf = depotMappingEntry.conf.updateIsSerialized(event);
		const updated = available.set(depotMappingEntry.conf.id, depotMappingEntry);
		this.filteredStructMapSubject.next(updated);
	}

	public disableDeselection(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
	}

	public updateDepotSchema(
		schema: Array<DepotSchemaEntity>,
		mapping: Map<DepotSchemaId, HeaderMappingItemEntity>
	): Array<DepotSchemaEntity> {
		return schema.reduce<Array<DepotSchemaEntity>>(
			(childrenOfSchemaEntry, currentDepotHeader) => {
				const headerConfig = mapping.get(currentDepotHeader.id);

				let updatedHeader = currentDepotHeader;

				if (
					currentDepotHeader.schemaElemType === SchemaDataStructure.Struct ||
					currentDepotHeader.schemaElemType === SchemaDataStructure.Array ||
					currentDepotHeader.schemaElemType === SchemaDataStructure.Root
				) {
					updatedHeader = updatedHeader.updateChildren(
						this.updateDepotSchema(
							updatedHeader.children as Array<DepotSchemaEntity>,
							mapping
						)
					);
				}

				if (
					headerConfig &&
					currentDepotHeader.currentLabel !== headerConfig.label
				) {
					updatedHeader = updatedHeader.updateCurrentLabel(headerConfig.label);
				}

				if (
					headerConfig &&
					currentDepotHeader.position !== headerConfig.position
				) {
					updatedHeader = updatedHeader.updatePosition(headerConfig.position);
				}

				if (
					headerConfig &&
					currentDepotHeader.convertTo !== headerConfig.convertTo
				) {
					updatedHeader = updatedHeader.updateConvertTo(headerConfig.convertTo);
				}

				if (headerConfig && currentDepotHeader.format !== headerConfig.format) {
					updatedHeader = updatedHeader.updateFormat(headerConfig.format);
				}

				if (
					headerConfig &&
					currentDepotHeader.timezone !== headerConfig.timezone
				) {
					updatedHeader = updatedHeader.updateTimezone(headerConfig.timezone);
				}

				if (
					headerConfig &&
					currentDepotHeader.language !== headerConfig.language
				) {
					updatedHeader = updatedHeader.updateLanguage(headerConfig.language);
				}

				childrenOfSchemaEntry.push(updatedHeader);
				return childrenOfSchemaEntry;
			},
			[]
		);
	}

	private flattenStructure(
		arr: Array<DepotSchemaEntity>
	): Map<DepotSchemaId, DepotMappingConfig> {
		return DepotSchemaEntity.flatten(arr).reduce(
			(acc, curr) =>
				acc.set(curr.id, {
					conf: curr,
					isEditActive: false,
					isFolded: false,
				}),
			Map<DepotSchemaId, DepotMappingConfig>()
		);
	}

	private updateOriginalSchema(
		schema: Array<DepotSchemaEntity>,
		mapping: Map<DepotSchemaId, DepotMappingConfig>
	): Array<DepotSchemaEntity> {
		return schema.reduce<Array<DepotSchemaEntity>>(
			(childrenOfSchemaEntry, currentDepotHeader) => {
				if (currentDepotHeader.schemaElemType === SchemaDataStructure.Leaf) {
					const headerConfig = mapping.get(
						currentDepotHeader.id
					) as DepotMappingConfig;
					childrenOfSchemaEntry.push(headerConfig.conf);
				} else if (
					currentDepotHeader.schemaElemType === SchemaDataStructure.Struct ||
					currentDepotHeader.schemaElemType === SchemaDataStructure.Array
				) {
					const headerConfigUserUpdates = mapping.get(
						currentDepotHeader.id
					) as DepotMappingConfig;
					const updatedEntry = headerConfigUserUpdates.conf.updateChildren(
						this.updateOriginalSchema(
							currentDepotHeader.children as Array<DepotSchemaEntity>,
							mapping
						)
					);
					childrenOfSchemaEntry.push(updatedEntry);
				}
				return childrenOfSchemaEntry;
			},
			[]
		);
	}

	private countSelectedItems(
		configMap: Map<DepotSchemaId, DepotMappingConfig>
	): number {
		return configMap.filter((l) => l.conf.isSelected).size;
	}

	private updateSchemaAnalysis(
		beforeUpdateLabel: string,
		headerDepotMapping: DepotSchemaEntity,
		currentSchema: Map<string, Set<string>>
	): Map<string, Set<string>> {
		let lookupLabel = beforeUpdateLabel;
		if (beforeUpdateLabel === '') {
			lookupLabel = EMPTY_SCHEMA_HEADER_LABEL;
		}
		let previousEntriesWithSameLabel = currentSchema.get(lookupLabel);
		if (previousEntriesWithSameLabel) {
			previousEntriesWithSameLabel = previousEntriesWithSameLabel.remove(
				`${headerDepotMapping.id}`
			);
			currentSchema = currentSchema.set(
				lookupLabel,
				previousEntriesWithSameLabel
			);
			const entryLabel = headerDepotMapping.currentLabel
				? headerDepotMapping.currentLabel
				: EMPTY_SCHEMA_HEADER_LABEL;
			const currentEntriesSameLabel = currentSchema.get(entryLabel);
			if (currentEntriesSameLabel) {
				const updatedEntriesSameLabel = currentEntriesSameLabel.add(
					`${headerDepotMapping.id}`
				);
				currentSchema = currentSchema.set(entryLabel, updatedEntriesSameLabel);
			} else {
				currentSchema = currentSchema.set(
					entryLabel,
					Set(`${headerDepotMapping.id}`)
				);
			}
		}
		return currentSchema;
	}

	private updateDuplicates(
		entry: DepotMappingConfig,
		currentDuplicates: Set<string>,
		schemaAnalysis: Map<string, Set<string>>,
		currentSchemaMap: Map<DepotSchemaId, DepotMappingConfig>
	): Set<string> {
		let updatedDuplicates = Set(currentDuplicates);
		/**
		 * get target schema entry AppDepotMapping entity instance
		 * and check if entry is selected
		 */
		const targetAppDepotMappingInst = entry.conf;
		const schemaEntriesWithSameLabel = targetAppDepotMappingInst.currentLabel
			? schemaAnalysis.get(targetAppDepotMappingInst.currentLabel)
			: schemaAnalysis.get(EMPTY_SCHEMA_HEADER_LABEL);
		if (!schemaEntriesWithSameLabel) {
			return updatedDuplicates; // this case should not exist since all entries are initialized to an empty Set
		}

		/**
		 * Check if at least one sibling is selected
		 * to disable validation and trigger warning
		 */
		if (
			targetAppDepotMappingInst.isSelected &&
			schemaEntriesWithSameLabel.size > 1
		) {
			/**
			 * updates setOfDuplicates Set
			 */
			const hasSameLabelEntriesSelected = schemaEntriesWithSameLabel
				.filter((e) => e !== targetAppDepotMappingInst.id)
				.reduce((acc, curr) => {
					const sameLabelSchemaEntry = currentSchemaMap.get(`${curr}`);
					if (sameLabelSchemaEntry) {
						if (sameLabelSchemaEntry.conf.isSelected) {
							updatedDuplicates = updatedDuplicates.add(
								`${sameLabelSchemaEntry.conf.id}`
							);
						}

						return acc || sameLabelSchemaEntry.conf.isSelected;
					} else {
						return acc;
					}
				}, false);
			if (hasSameLabelEntriesSelected) {
				updatedDuplicates = updatedDuplicates.add(
					`${targetAppDepotMappingInst.id}`
				);
			}
		}
		return updatedDuplicates;
	}

	private hasSelectedEmptyLabelHeaders(
		schemaAnalysis: Map<string, Set<string>>,
		currentMap: Map<DepotSchemaId, DepotMappingConfig>
	): boolean {
		const emptyLabelSchemaEntries = schemaAnalysis.get(
			EMPTY_SCHEMA_HEADER_LABEL
		);
		if (!emptyLabelSchemaEntries) {
			return false;
		}
		// we start with false as we are not sure if any empty label is selected
		// we use ids of assembled empty label entries and check within the map if selected
		return emptyLabelSchemaEntries.reduce((result, entryId) => {
			const mapping = currentMap.get(`${entryId}`);
			if (!mapping) {
				return result;
			}
			return result || mapping.conf.isSelected;
		}, false);
	}

	protected filterSelectedEntriesByAppearanceWithinStructure(
		structure: Array<DepotSchemaEntity>,
		currentMap: Map<DepotSchemaId, DepotMappingConfig>,
		alreadySelected: Array<DepotSchemaEntity> = []
	): Array<DepotSchemaEntity> {
		return structure.reduce((selection, header) => {
			const conf = currentMap.get(header.id)?.conf;
			if (conf && conf.isSelected) {
				alreadySelected.push(conf);
			}
			if (
				header.schemaElemType === SchemaDataStructure.Struct ||
				header.schemaElemType === SchemaDataStructure.Array
			) {
				return this.filterSelectedEntriesByAppearanceWithinStructure(
					header.children as Array<DepotSchemaEntity>,
					currentMap,
					alreadySelected
				);
			} else if (header.schemaElemType === SchemaDataStructure.Root) {
				return this.filterSelectedEntriesByAppearanceWithinStructure(
					header.children as Array<DepotSchemaEntity>,
					currentMap,
					alreadySelected
				);
			}
			return alreadySelected;
		}, alreadySelected);
	}
}

@NgModule({
	imports: [
		CommonModule,
		FormsModule,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatButtonModule,
		MatCheckboxModule,
		MatTooltipModule,
		DcTruncatePipeModule,
		DcColumnTypeModule,
		DcButtonModule,
		DcHighlightDirectiveModule,
		MatSlideToggleModule,
	],
	declarations: [DepotSchemaViewerComponent],
	exports: [DepotSchemaViewerComponent],
})
export class DcCoreDepotSchemaViewerModule {}
